import { getLocalTimeZone, today } from "@internationalized/date";
import {
  AttachmentCategoryEnum,
  formatAllowsMultipleDesigns,
  ModelsEnum,
} from "@nlc/shared-types";
import { CloudUpload } from "lucide-react";
import { useRef } from "react";

import type { ManagedFile } from "@/components/file-uploader/types";

import { Button } from "@/components/button";
import { FileUploadDropZone } from "@/components/file-uploader/components/FileUploadDropZone";
import { FileUploadList } from "@/components/file-uploader/components/FileUploadList";
import { FileUploadTrigger } from "@/components/file-uploader/components/FileUploadTrigger";
import { FileUploaderProvider } from "@/components/file-uploader/FileUploaderContext";

import type { PostType } from "../post-type-step/postTypes";
import type { DesignFile } from "./types";

import { acceptedDesignExtensions } from "./acceptedDesignExtensions";

const MAX_FILE_SIZE_MB = 200;

const EXTENSION_LABELS: Record<string, string | undefined> = {
  ".jpg": "JPG",
  ".mov": "MOV",
  ".mp4": "MP4",
  ".png": "PNG",
  ".webp": "WebP",
};

export interface UploadsChange {
  added: DesignFile[];
  removedIds: number[];
}

export function DesignUploader({
  brandId,
  onUploadsChange,
  postType,
}: {
  brandId: number;
  onUploadsChange: (change: UploadsChange) => void;
  postType: PostType;
}) {
  const uploadedIds = useRef(new Set<number>());
  const acceptedExtensions = acceptedDesignExtensions(postType);

  function syncUploads(files: ManagedFile[]) {
    const presentIds = new Set<number>();
    const added: DesignFile[] = [];

    for (const { attachmentId, file, status } of files) {
      if (status !== "success" || !attachmentId || !file) continue;

      presentIds.add(attachmentId);
      if (!uploadedIds.current.has(attachmentId)) {
        uploadedIds.current.add(attachmentId);
        added.push(toDesign(attachmentId, file));
      }
    }

    const removedIds = [...uploadedIds.current].filter(
      (id) => !presentIds.has(id),
    );

    for (const id of removedIds) uploadedIds.current.delete(id);

    if (added.length > 0 || removedIds.length > 0) {
      onUploadsChange({ added, removedIds });
    }
  }

  return (
    <FileUploaderProvider
      acceptedFileTypes={acceptedExtensions}
      allowsMultiple={formatAllowsMultipleDesigns(postType.format)}
      category={AttachmentCategoryEnum.SOCIAL_PUBLISHING}
      maxFileSize={MAX_FILE_SIZE_MB * 1024 * 1024}
      onFilesChange={syncUploads}
      // The post doesn't exist yet, so the brand only scopes the S3 key; the post becomes the parent once it's created.
      parentId={brandId}
      parentType={ModelsEnum.SOCIAL_PUBLISHING}
    >
      <div className="flex flex-col gap-md">
        <FileUploadDropZone
          className={({ isDropTarget }) =>
            `rounded-2xl border border-dashed border-border-brand transition-colors ${
              isDropTarget ? "bg-bg-brand-secondary" : "bg-bg-brand-primary"
            }`
          }
        >
          <FileUploadTrigger>
            <Button
              className="flex min-h-[100px] w-full items-center justify-center gap-3xl px-3xl py-xl outline-hidden"
              variant="unstyled"
            >
              <span className="grid size-10 shrink-0 place-items-center rounded-full bg-bg-brand-solid">
                <CloudUpload className="text-white" size={20} />
              </span>
              <span className="flex flex-col text-left">
                <span className="text-md font-semibold text-text-primary">
                  Click to upload or drop files here
                </span>
                <span className="text-sm text-text-tertiary">
                  {acceptedTypesText(acceptedExtensions)}, up to{" "}
                  {MAX_FILE_SIZE_MB} MB each
                </span>
              </span>
            </Button>
          </FileUploadTrigger>
        </FileUploadDropZone>

        <FileUploadList enableRetry />
      </div>
    </FileUploaderProvider>
  );
}

function acceptedTypesText(extensions: string[]): string {
  const labels = extensions
    .map((extension) => EXTENSION_LABELS[extension])
    .filter(Boolean);

  return labels.length > 1
    ? `${labels.slice(0, -1).join(", ")} or ${labels.at(-1)}`
    : (labels[0] ?? "");
}

function toDesign(attachmentId: number, file: File): DesignFile {
  return {
    createdAt: today(getLocalTimeZone()).toString(),
    extension: file.name.split(".").pop() ?? "",
    id: attachmentId,
    isDirectory: false,
    name: file.name,
    size: file.size,
    type: "file",
    url: URL.createObjectURL(file),
  };
}
